import { useCurrentUser } from "hooks/auth";
import React, { useState, useEffect } from "react";
import Joyride, {
	CallBackProps,
	STATUS,
	ACTIONS,
	EVENTS,
	Step,
} from "react-joyride";
import {
	ExtendedCookieAttributes,
	getCookieV2,
	removeCookie,
	setCookie,
} from "utils/cookiesHelper";
import { logIfDebug } from "utils/logging";

export interface BaseTourProps {
	isVisible?: boolean;
	onTourComplete?: () => void;
}

interface TourProps extends BaseTourProps {
	cookieName: string;
	tourSteps: Step[];
}

const setCookieValue = (cookieName: string) => {
	setCookie(cookieName, "", {
		keepCookie: true,
		expires: 180,
	} as ExtendedCookieAttributes);
};

const getCookieName = (
	cookieName: string,
	id: number | null | undefined,
	isGuest: boolean
) => {
	return `${cookieName}${id ?? ""}${isGuest ? "g" : "u"}`;
};

export const GuidedTour: React.FC<TourProps> = ({
	isVisible = true,
	onTourComplete,
	cookieName,
	tourSteps,
}) => {
	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);
	const { currentUser, isGuest } = useCurrentUser();
	const userCookie = getCookieName(
		cookieName,
		currentUser?.id,
		isGuest
	);

	useEffect(() => {
		// Check if user has chosen to not see the tour again
		const dontShowAgain = getCookieV2(userCookie);

		logIfDebug("RFx Tour Debug:", { dontShowAgain, isVisible });

		if (!dontShowAgain && isVisible) {
			// Small delay to ensure DOM elements are rendered
			const timer = setTimeout(() => {
				setRun(true);
			}, 500);

			return () => clearTimeout(timer);
		}
	}, [isVisible, userCookie]);

	const createDoNotShowCookie = () => {
		setCookieValue(userCookie);
	};

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, type, index, action } = data;

		logIfDebug("Joyride callback:", {
			status,
			type,
			index,
			action,
			stepIndex,
		});

		if (action === "close") {
			setRun(false);
		}

		if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
			// Save preference if checkbox was checked OR if user clicked skip
			if (dontShowAgainChecked || status === STATUS.SKIPPED) {
				createDoNotShowCookie();
			}

			setRun(false);
			setStepIndex(0);
			setDontShowAgainChecked(false);

			if (onTourComplete) {
				onTourComplete();
			}
		} else if (type === EVENTS.STEP_AFTER) {
			// Handle step navigation
			if (action === ACTIONS.NEXT) {
				setStepIndex(index + 1);
			} else if (action === ACTIONS.PREV) {
				setStepIndex(index - 1);
			}
		} else if (type === EVENTS.STEP_BEFORE) {
			// Update step index before showing step
			setStepIndex(index);
		}
	};

	// Custom tooltip component with checkbox
	const CustomTooltip = ({ tooltipProps, step, index, size }: any) => {
		const isLastStep = index === size - 1;

		return (
			<div
				style={{
					backgroundColor: "#fff",
					borderRadius: "8px",
					padding: "20px",
					maxWidth: "400px",
					boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
				}}
			>
				{/* Progress indicator */}
				<div
					style={{ marginBottom: "16px", fontSize: "12px", color: "#6B7280" }}
				>
					{index + 1} of {size}
				</div>

				{/* Step content */}
				<div>{step.content}</div>

				{/* Checkbox for "don't show again" on last step */}
				{isLastStep && (
					<div
						style={{
							margin: "16px 0",
							display: "flex",
							alignItems: "center",
							gap: "8px",
						}}
					>
						<input
							type="checkbox"
							id="dont-show-again"
							checked={dontShowAgainChecked}
							onChange={(e) => setDontShowAgainChecked(e.target.checked)}
							style={{ margin: 0 }}
						/>
						<label
							htmlFor="dont-show-again"
							style={{ fontSize: "14px", color: "#333", cursor: "pointer" }}
						>
							Don't show this tour again
						</label>
					</div>
				)}

				{/* Buttons */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: "20px",
					}}
				>
					<div>
						{index > 0 && (
							<button
								onClick={() => {
									logIfDebug("Back button clicked");
									// Manually trigger back navigation
									setStepIndex(Math.max(0, stepIndex - 1));
								}}
								style={{
									background: "none",
									border: "none",
									color: "#6B7280",
									fontSize: "14px",
									cursor: "pointer",
									marginRight: "8px",
								}}
							>
								Back
							</button>
						)}
					</div>

					<div style={{ display: "flex", gap: "8px" }}>
						<button
							onClick={() => {
								logIfDebug("Next/Close button clicked");
								if (isLastStep) {
									// Handle close
									if (dontShowAgainChecked) {
										createDoNotShowCookie();
									}
									setRun(false);
									setStepIndex(0);
									setDontShowAgainChecked(false);
									if (onTourComplete) {
										onTourComplete();
									}
								} else {
									// Go to next step
									setStepIndex(stepIndex + 1);
								}
							}}
							style={{
								backgroundColor: "#4F46E5",
								borderRadius: "6px",
								color: "#fff",
								fontSize: "14px",
								fontWeight: 600,
								padding: "8px 16px",
								border: "none",
								cursor: "pointer",
							}}
						>
							{isLastStep ? "Close" : "Next"}
						</button>
					</div>
				</div>
			</div>
		);
	};

	if (!run) {
		return null;
	}

	return (
		<Joyride
			steps={tourSteps}
			run={run}
			stepIndex={stepIndex}
			continuous={true}
			showProgress={false} // We're handling progress in custom tooltip
			showSkipButton={false} // We're handling skip in custom tooltip
			callback={handleJoyrideCallback}
			scrollToFirstStep={true}
			scrollOffset={100}
			scrollDuration={50}
			tooltipComponent={CustomTooltip}
			styles={{
				options: {
					arrowColor: "#fff",
					backgroundColor: "#fff",
					beaconSize: 36,
					overlayColor: "rgba(0, 0, 0, 0.5)",
					primaryColor: "#0045F5",
					spotlightShadow: "0 0 0 4px rgba(0, 69, 245, 0.8)",
					textColor: "#333",
					width: 400,
					zIndex: 1000,
				},
				spotlight: {
					borderRadius: "4px",
					boxShadow: "0 0 0 4px rgba(0, 69, 245, 0.9)",
				},
			}}
			locale={{
				back: "Back",
				close: "Close",
				last: "Close",
				next: "Next",
				skip: "Skip",
			}}
			disableOverlayClose={true}
			disableScrollParentFix={true}
			spotlightClicks={false}
			spotlightPadding={4}
			disableOverlay={false}
			hideBackButton={true} // We're handling this in custom tooltip
			floaterProps={{
				disableAnimation: false,
			}}
		/>
	);
};

// Hook for easy integration
export const useGuidedTour = (cookieName: string) => {
	const [isTourVisible, setIsTourVisible] = useState(false);
	const { currentUser, isGuest } = useCurrentUser();

	const userCookie = getCookieName(
		cookieName,
		currentUser?.id,
		isGuest
	);

	const startTour = () => {
		setIsTourVisible(true);
	};

	const hideTour = () => {
		setIsTourVisible(false);
	};

	const dontShowAgain = () => {
		setCookieValue(userCookie);
		setIsTourVisible(false);
	};

	const shouldShowTour = () => {
		let cookie = getCookieV2(userCookie);
		return cookie ? false : true;
	};

	const resetTourPreference = () => {
		removeCookie(userCookie);
	};

	return {
		isTourVisible,
		setIsTourVisible,
		startTour,
		hideTour,
		dontShowAgain,
		shouldShowTour,
		resetTourPreference,
	};
};

export default GuidedTour;
